import * as React from 'react';
import { useLocation } from 'react-router-dom';
import { alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Chip from '@mui/material/Chip';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Grid from '@mui/material/Grid';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import CssBaseline from '@mui/material/CssBaseline';
import SearchIcon from '@mui/icons-material/Search';
import StarIcon from '@mui/icons-material/Star';
import GrassIcon from '@mui/icons-material/Grass';
import TuneIcon from '@mui/icons-material/Tune';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import AppNavbar from '../dashboard/components/AppNavbar';
import SideMenu from '../dashboard/components/SideMenu';
import AppTheme from '../shared-theme/AppTheme';
import { DataModeProvider, DATA_MODES } from '../context/DataModeContext';
import { GrowProvider } from '../dashboard/context/GrowContext';
import { CommunityProvider, useCommunity } from '../context/CommunityContext';
import {
  chartsCustomizations,
  dataGridCustomizations,
  datePickersCustomizations,
  treeViewCustomizations,
} from '../dashboard/theme/customizations';

const xThemeComponents = {
  ...chartsCustomizations,
  ...dataGridCustomizations,
  ...datePickersCustomizations,
  ...treeViewCustomizations,
};

function GrowCard({ grow }) {
  return (
    <Card
      variant="outlined"
      sx={{
        p: 2,
        cursor: 'pointer',
        transition: 'all 0.2s',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: 3,
        },
      }}
    >
      <Stack spacing={1.5}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, fontFamily: 'Inter, sans-serif' }}>
            {grow.name}
          </Typography>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <StarIcon sx={{ fontSize: 16, color: 'warning.main' }} />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{grow.rating}</Typography>
          </Stack>
        </Box>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          by {grow.author} • {grow.reviews} reviews
        </Typography>
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
          <Chip label={grow.plantType} size="small" color="primary" variant="outlined" />
          <Chip label={grow.medium} size="small" variant="outlined" />
        </Stack>
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
          {grow.tags.slice(0, 3).map((tag) => (
            <Chip key={tag} label={tag} size="small" sx={{ fontSize: '0.65rem' }} />
          ))}
        </Stack>
      </Stack>
    </Card>
  );
}

function PresetCard({ preset }) {
  return (
    <Card
      variant="outlined"
      sx={{
        p: 2,
        cursor: 'pointer',
        transition: 'all 0.2s',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: 3,
        },
      }}
    >
      <Stack spacing={1}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, fontFamily: 'Inter, sans-serif' }}>
            {preset.name}
          </Typography>
          <TuneIcon sx={{ fontSize: 18, color: 'primary.main' }} />
        </Box>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          by {preset.author}
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip label={preset.plantType} size="small" variant="outlined" />
          <Chip label={preset.stage} size="small" />
        </Stack>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {preset.downloads.toLocaleString()} downloads
        </Typography>
      </Stack>
    </Card>
  );
}

function CollectiveContent() {
  const { filterGrows, filterPresets, loading } = useCommunity();
  const [activeTab, setActiveTab] = React.useState(0);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [plantFilter, setPlantFilter] = React.useState('all');
  const [mediumFilter, setMediumFilter] = React.useState('all');

  // Filter grows based on current filters
  const filteredGrows = React.useMemo(() => {
    return filterGrows({
      plantType: plantFilter,
      medium: mediumFilter,
      search: searchQuery,
    });
  }, [filterGrows, plantFilter, mediumFilter, searchQuery]);

  // Filter presets based on current filters
  const filteredPresets = React.useMemo(() => {
    return filterPresets({
      plantType: plantFilter,
      search: searchQuery,
    });
  }, [filterPresets, plantFilter, searchQuery]);

  // Get trending, top rated, and new grows
  const trendingGrows = filteredGrows.slice(0, 4);
  const topRatedGrows = [...filteredGrows].sort((a, b) => b.rating - a.rating).slice(0, 4);
  const newGrows = [...filteredGrows].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).slice(0, 2);

  // Get popular presets
  const popularPresets = [...filteredPresets].sort((a, b) => b.downloads - a.downloads);

  if (loading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography sx={{ color: 'text.secondary' }}>Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', maxWidth: { sm: '100%', md: '1400px' }, mx: 'auto', p: 3 }}>
      {/* Header */}
      <Stack spacing={1} sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, fontFamily: 'Inter, sans-serif' }}>
          The Collective
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary' }}>
          Discover top-rated grows, share your experience, and find the perfect sensor presets
        </Typography>
      </Stack>

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onChange={(e, v) => setActiveTab(v)}
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab
          icon={<GrassIcon sx={{ fontSize: 18 }} />}
          iconPosition="start"
          label="Grows"
          sx={{ textTransform: 'none', fontFamily: 'Inter, sans-serif' }}
        />
        <Tab
          icon={<TuneIcon sx={{ fontSize: 18 }} />}
          iconPosition="start"
          label="Sensor Presets"
          sx={{ textTransform: 'none', fontFamily: 'Inter, sans-serif' }}
        />
      </Tabs>

      {/* Search and Filters */}
      <Card variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
          <TextField
            placeholder={activeTab === 0 ? "Search grows..." : "Search presets..."}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            sx={{ flex: 1, minWidth: 200 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
          />
          <Stack direction="row" spacing={2}>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Plant Type</InputLabel>
              <Select
                value={plantFilter}
                label="Plant Type"
                onChange={(e) => setPlantFilter(e.target.value)}
              >
                <MenuItem value="all">All Plants</MenuItem>
                <MenuItem value="cucumber">Cucumber</MenuItem>
                <MenuItem value="tomato">Tomato</MenuItem>
                <MenuItem value="pepper">Pepper</MenuItem>
                <MenuItem value="lettuce">Lettuce</MenuItem>
                <MenuItem value="herbs">Herbs</MenuItem>
              </Select>
            </FormControl>
            {activeTab === 0 && (
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Medium</InputLabel>
                <Select
                  value={mediumFilter}
                  label="Medium"
                  onChange={(e) => setMediumFilter(e.target.value)}
                >
                  <MenuItem value="all">All Mediums</MenuItem>
                  <MenuItem value="hydroponic">Hydroponic</MenuItem>
                  <MenuItem value="soil">Soil</MenuItem>
                  <MenuItem value="coco">Coco Coir</MenuItem>
                  <MenuItem value="aeroponic">Aeroponic</MenuItem>
                </Select>
              </FormControl>
            )}
          </Stack>
        </Stack>
      </Card>

      {/* Grows Tab Content */}
      {activeTab === 0 && (
        <Stack spacing={3}>
          {/* Trending Section */}
          <Box>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
              <TrendingUpIcon sx={{ color: 'success.main' }} />
              <Typography variant="h6" sx={{ fontWeight: 600, fontFamily: 'Inter, sans-serif' }}>
                Trending Grows
              </Typography>
            </Stack>
            <Grid container spacing={2}>
              {trendingGrows.map((grow) => (
                <Grid item xs={12} sm={6} md={3} key={grow.id}>
                  <GrowCard grow={grow} />
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Top Rated Section */}
          <Box>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
              <StarIcon sx={{ color: 'warning.main' }} />
              <Typography variant="h6" sx={{ fontWeight: 600, fontFamily: 'Inter, sans-serif' }}>
                Top Rated
              </Typography>
            </Stack>
            <Grid container spacing={2}>
              {topRatedGrows.map((grow) => (
                <Grid item xs={12} sm={6} md={3} key={grow.id}>
                  <GrowCard grow={grow} />
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* New This Week Section */}
          <Box>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
              <NewReleasesIcon sx={{ color: 'info.main' }} />
              <Typography variant="h6" sx={{ fontWeight: 600, fontFamily: 'Inter, sans-serif' }}>
                New This Week
              </Typography>
            </Stack>
            <Grid container spacing={2}>
              {newGrows.map((grow) => (
                <Grid item xs={12} sm={6} md={3} key={grow.id}>
                  <GrowCard grow={grow} />
                </Grid>
              ))}
            </Grid>
          </Box>
        </Stack>
      )}

      {/* Presets Tab Content */}
      {activeTab === 1 && (
        <Stack spacing={3}>
          {/* Popular Presets */}
          <Box>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
              <TrendingUpIcon sx={{ color: 'success.main' }} />
              <Typography variant="h6" sx={{ fontWeight: 600, fontFamily: 'Inter, sans-serif' }}>
                Popular Presets
              </Typography>
            </Stack>
            <Grid container spacing={2}>
              {popularPresets.map((preset) => (
                <Grid item xs={12} sm={6} md={4} key={preset.id}>
                  <PresetCard preset={preset} />
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* By Growth Stage */}
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, fontFamily: 'Inter, sans-serif', mb: 2 }}>
              Browse by Growth Stage
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {['Seedling', 'Vegetative', 'Flowering', 'Fruiting', 'Harvest'].map((stage) => (
                <Chip
                  key={stage}
                  label={stage}
                  clickable
                  sx={{ fontFamily: 'Inter, sans-serif' }}
                />
              ))}
            </Stack>
          </Box>

          {/* Coming Soon Notice */}
          <Card variant="outlined" sx={{ p: 3, textAlign: 'center', bgcolor: 'action.hover' }}>
            <Typography variant="body1" sx={{ color: 'text.secondary', fontFamily: 'Inter, sans-serif' }}>
              More presets coming soon! Share your own presets with the community.
            </Typography>
          </Card>
        </Stack>
      )}
    </Box>
  );
}

export default function TheCollectivePage(props) {
  const location = useLocation();
  const isDemo = location.pathname.startsWith('/demo');

  return (
    <AppTheme {...props} themeComponents={xThemeComponents}>
      <CssBaseline enableColorScheme />
      <Box sx={{ display: 'flex' }}>
        <DataModeProvider mode={isDemo ? DATA_MODES.DEMO : DATA_MODES.LIVE}>
          <GrowProvider>
            <CommunityProvider>
              <SideMenu />
              <AppNavbar />
              <Box
                component="main"
                sx={(theme) => ({
                  flexGrow: 1,
                  backgroundColor: theme.vars
                    ? `rgba(${theme.vars.palette.background.defaultChannel} / 1)`
                    : alpha(theme.palette.background.default, 1),
                  overflow: 'auto',
                  minHeight: '100vh',
                })}
              >
                <Stack
                  sx={{
                    pt: { xs: 10, md: 3 },
                    pb: 5,
                  }}
                >
                  <CollectiveContent />
                </Stack>
              </Box>
            </CommunityProvider>
          </GrowProvider>
        </DataModeProvider>
      </Box>
    </AppTheme>
  );
}
